import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Accordion,
  ActionIcon,
  Badge,
  Collapse,
  Menu,
  Select,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { GripVertical, Info } from 'lucide-react'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_CUSTOM_QUESTIONS } from 'src/graphql/customQuestions.graphql'
import {
  CustomQuestionFieldType,
  CustomQuestionVisibility,
  formatCustomQuestionFieldType,
} from 'src/graphql/types/customQuestions'
import IconAdd from 'src/icons/IconAdd'
import IconCheckmark from 'src/icons/IconCheckmark'
import IconClose from 'src/icons/IconClose'
import IconEdit from 'src/icons/IconEdit'

export interface QuestionOption {
  id: string
  label: string
  order: number
}

export interface QuestionConfig {
  id: string
  customQuestionId?: string
  label: string
  key?: string
  fieldType?: string
  isRequired?: boolean
  /**
   * PUBLIC ("Short form") questions appear on the public interest form and
   * sync to Indeed as screener questions. PRIVATE ("DOT application")
   * questions only appear inside the full application.
   */
  visibility?: CustomQuestionVisibility
  options?: QuestionOption[]
  originalOptions?: QuestionOption[] // Track original options to detect removals
  order: number
  isOpen: boolean
}

// Constants for validation
const QUESTION_LABEL_MAX_LENGTH = 1000
const QUESTION_LABEL_MIN_LENGTH = 2
const QUESTION_KEY_MAX_LENGTH = 100

// Truncate text to a specific length
const truncateText = (text: string, maxLength = 50): string => {
  if (!text || text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

// Sortable question item component
const SortableQuestionItem = ({
  question,
  onLabelChange,
  onKeyChange,
  onFieldTypeChange,
  onIsRequiredChange,
  onVisibilityChange,
  onOptionsChange,
  onToggle,
  onDelete,
}: {
  question: QuestionConfig
  onLabelChange: (id: string, label: string) => void
  onKeyChange: (id: string, key: string) => void
  onFieldTypeChange: (id: string, fieldType: string) => void
  onIsRequiredChange: (id: string, isRequired: boolean) => void
  onVisibilityChange: (id: string, visibility: CustomQuestionVisibility) => void
  onOptionsChange: (id: string, options: QuestionOption[]) => void
  onToggle: (id: string) => void
  onDelete: (id: string) => void
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  // Validation for question fields
  const isQuestionValid = () => {
    // Check label
    if (
      !question.label ||
      question.label.trim().length < QUESTION_LABEL_MIN_LENGTH ||
      question.label.length > QUESTION_LABEL_MAX_LENGTH
    ) {
      return false
    }
    // Check field type
    if (!question.fieldType) return false
    // Check required status (explicitly check for boolean values)
    if (typeof question.isRequired !== 'boolean') {
      return false
    }
    // Check options for select types
    if (
      question.fieldType === CustomQuestionFieldType.SINGLE_SELECT ||
      question.fieldType === CustomQuestionFieldType.MULTI_SELECT
    ) {
      if (!question.options || question.options.length < 2) {
        return false
      }
      // Check that all options have labels
      if (
        question.options.some(
          (opt) => !opt.label || opt.label.trim().length === 0
        )
      ) {
        return false
      }
    }
    return true
  }

  const getValidationTooltip = () => {
    if (!question.label || question.label.trim().length < QUESTION_LABEL_MIN_LENGTH) {
      return `Question must be at least ${QUESTION_LABEL_MIN_LENGTH} characters`
    }
    if (question.label.length > QUESTION_LABEL_MAX_LENGTH) {
      return `Question cannot exceed ${QUESTION_LABEL_MAX_LENGTH} characters`
    }
    if (!question.fieldType) {
      return 'Please select a question type'
    }
    if (typeof question.isRequired !== 'boolean') {
      return 'Please select if question is required'
    }
    if (
      question.fieldType === CustomQuestionFieldType.SINGLE_SELECT ||
      question.fieldType === CustomQuestionFieldType.MULTI_SELECT
    ) {
      if (!question.options || question.options.length < 2) {
        return 'Please add at least 2 options'
      }
      if (
        question.options.some(
          (opt) => !opt.label || opt.label.trim().length === 0
        )
      ) {
        return 'All options must have a label'
      }
    }
    return ''
  }

  const handleAddOption = () => {
    const newOptions = question.options || []
    const newOption: QuestionOption = {
      id: `option-${Date.now()}`,
      label: '',
      order: newOptions.length,
    }
    onOptionsChange(question.id, [...newOptions, newOption])
  }

  const handleOptionLabelChange = (optionId: string, label: string) => {
    const updatedOptions = (question.options || []).map((opt) =>
      opt.id === optionId ? { ...opt, label } : opt
    )
    onOptionsChange(question.id, updatedOptions)
  }

  const handleRemoveOption = (optionId: string) => {
    const updatedOptions = (question.options || []).filter(
      (opt) => opt.id !== optionId
    )
    onOptionsChange(question.id, updatedOptions)
  }

  const showOptions =
    question.fieldType === CustomQuestionFieldType.SINGLE_SELECT ||
    question.fieldType === CustomQuestionFieldType.MULTI_SELECT

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex w-full flex-col rounded ${
        isDragging ? 'bg-gray-100' : ''
      }`}
    >
      <div className="flex w-full flex-row items-center justify-between p-1">
        <div className="flex flex-1 items-center gap-2 overflow-hidden">
          <div
            {...attributes}
            {...listeners}
            className="flex-shrink-0 cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing"
          >
            <GripVertical size={16} />
          </div>
          <Tooltip label={question.label || 'New Question'} withArrow>
            <Text className="min-w-0 text-sm text-doubleNickel-gray-800">
              {truncateText(question.label || 'New Question', 35)}
              {question.isRequired && (
                <span className="ml-1 text-doubleNickel-error-500">*</span>
              )}
            </Text>
          </Tooltip>
        </div>
        {/* Where the question is asked. Identical Badge treatment to the
            field-type badge beside it — neutral gray for both locations, so
            the row reads as metadata rather than status. */}
        <Badge
          variant="light"
          color="gray"
          size="sm"
          className="mr-2 flex-shrink-0"
        >
          {question.visibility === CustomQuestionVisibility.PUBLIC
            ? 'Short form'
            : 'DOT Application'}
        </Badge>
        {question.fieldType && (
          <Badge
            variant="light"
            color="gray"
            size="sm"
            className="mr-2 flex-shrink-0"
          >
            {formatCustomQuestionFieldType(question.fieldType)}
          </Badge>
        )}
        <div className="flex items-center gap-1">
          <Tooltip
            label={
              question.isOpen && !isQuestionValid()
                ? getValidationTooltip()
                : question.isOpen
                ? 'Save question'
                : 'Edit question'
            }
            withArrow
            disabled={question.isOpen && !isQuestionValid() ? false : undefined}
          >
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label={question.isOpen ? 'Save question' : 'Edit question'}
              onClick={() => {
                if (question.isOpen && !isQuestionValid()) {
                  return
                }
                onToggle(question.id)
              }}
              disabled={question.isOpen && !isQuestionValid()}
            >
              {question.isOpen ? (
                <IconCheckmark className="h-3 fill-none stroke-doubleNickel-gray-500" />
              ) : (
                <IconEdit className="h-3 fill-none stroke-doubleNickel-gray-500" />
              )}
            </ActionIcon>
          </Tooltip>
          <ActionIcon
            variant="subtle"
            size="sm"
            aria-label="Delete question"
            onClick={() => onDelete(question.id)}
          >
            <IconClose className="h-4 fill-none stroke-doubleNickel-gray-500" />
          </ActionIcon>
        </div>
      </div>
      <Collapse in={question.isOpen}>
        <div className="flex flex-col gap-3 pl-2 pt-2">
          <div className="flex flex-row items-center justify-between">
            <Text size="xs">
              Question
              <span className="text-doubleNickel-error-500">{' *'}</span>
            </Text>
            <div className="w-[250px]">
              <TextInput
                placeholder="Enter a question"
                value={question.label}
                onChange={(e) =>
                  onLabelChange(question.id, e.currentTarget.value)
                }
                size="xs"
                classNames={{
                  input: 'text-xs text-right',
                }}
                error={
                  question.label &&
                  (question.label.length < QUESTION_LABEL_MIN_LENGTH ||
                    question.label.length > QUESTION_LABEL_MAX_LENGTH)
                }
                maxLength={QUESTION_LABEL_MAX_LENGTH}
              />
            </div>
          </div>
          <div className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-1">
              <Text size="xs">Key</Text>
              <Tooltip
                label="Used as part of integrations with external systems. Only alphanumeric characters, underscores, and hyphens are allowed."
                withArrow
                position="top"
                multiline
                w={220}
              >
                <Info size={12} className="text-doubleNickel-gray-400" />
              </Tooltip>
            </div>
            <div className="w-[250px]">
              <TextInput
                placeholder="Enter a key (optional)"
                value={question.key || ''}
                onChange={(e) => {
                  // Only allow alphanumeric, underscore, and hyphen characters
                  const sanitized = e.currentTarget.value.replace(
                    /[^a-zA-Z0-9_-]/g,
                    ''
                  )
                  onKeyChange(question.id, sanitized)
                }}
                size="xs"
                classNames={{
                  input: 'text-xs text-right',
                }}
                error={
                  question.key &&
                  (!/^[a-zA-Z0-9_-]*$/.test(question.key) ||
                    question.key.length > QUESTION_KEY_MAX_LENGTH)
                }
                maxLength={QUESTION_KEY_MAX_LENGTH}
              />
            </div>
          </div>
          <div className="flex flex-row items-center justify-between">
            <Text size="xs">
              Required
              <span className="text-doubleNickel-error-500">{' *'}</span>
            </Text>
            <div className="w-[250px]">
              <Select
                placeholder="Required?"
                value={question.isRequired ? 'true' : 'false'}
                onChange={(value) =>
                  onIsRequiredChange(question.id, value === 'true')
                }
                data={[
                  { value: 'false', label: 'No' },
                  { value: 'true', label: 'Yes' },
                ]}
                size="xs"
                classNames={{
                  input: 'text-xs text-right',
                }}
              />
            </div>
          </div>
          <div className="flex flex-row items-center justify-between">
            <Text size="xs">
              Type
              <span className="text-doubleNickel-error-500">{' *'}</span>
            </Text>
            <div className="w-[250px]">
              <Select
                placeholder="Select field type"
                value={question.fieldType || null}
                onChange={(value) => onFieldTypeChange(question.id, value)}
                data={Object.values(CustomQuestionFieldType)
                  .filter((type) => type !== CustomQuestionFieldType.LONG_TEXT)
                  .map((type) => ({
                    value: type,
                    label: formatCustomQuestionFieldType(type),
                  }))}
                size="xs"
                disabled={!!question.customQuestionId}
                searchable
                classNames={{
                  input: 'text-xs text-right',
                }}
              />
            </div>
          </div>
          <div className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-1">
              <Text size="xs">Question location</Text>
              <Tooltip
                label="Short form questions appear on the public interest form, before the driver creates an account. DOT Application questions only appear inside the full application."
                withArrow
                position="top"
                multiline
                w={260}
              >
                <Info size={12} className="text-doubleNickel-gray-400" />
              </Tooltip>
            </div>
            <div className="w-[250px]">
              <Select
                aria-label="Question location"
                value={
                  question.visibility === CustomQuestionVisibility.PUBLIC
                    ? CustomQuestionVisibility.PUBLIC
                    : CustomQuestionVisibility.PRIVATE
                }
                onChange={(value) =>
                  onVisibilityChange(
                    question.id,
                    value === CustomQuestionVisibility.PUBLIC
                      ? CustomQuestionVisibility.PUBLIC
                      : CustomQuestionVisibility.PRIVATE
                  )
                }
                data={[
                  {
                    value: CustomQuestionVisibility.PRIVATE,
                    label: 'DOT Application',
                  },
                  {
                    value: CustomQuestionVisibility.PUBLIC,
                    label: 'Short form',
                  },
                ]}
                size="xs"
                allowDeselect={false}
                classNames={{
                  input: 'text-xs text-right',
                }}
              />
            </div>
          </div>
          {showOptions && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <Text size="xs">
                  Options
                  <span className="text-doubleNickel-error-500">{' *'}</span>
                </Text>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={handleAddOption}
                  className="text-doubleNickel-gray-600"
                >
                  <IconAdd
                    className="h-4 w-4"
                    stroke="currentColor"
                    fill="none"
                  />
                </ActionIcon>
              </div>
              <div className="space-y-2">
                {(question.options || []).map((option, index) => (
                  <div key={option.id} className="flex items-center gap-2 pl-2">
                    <Text size="xs" className="text-doubleNickel-gray-600">
                      {index + 1}.
                    </Text>
                    <TextInput
                      placeholder="Enter option"
                      value={option.label}
                      onChange={(e) =>
                        handleOptionLabelChange(
                          option.id,
                          e.currentTarget.value
                        )
                      }
                      size="xs"
                      className="flex-1"
                      classNames={{
                        input: 'text-xs',
                      }}
                    />
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={() => handleRemoveOption(option.id)}
                    >
                      <IconClose className="h-4 fill-none stroke-doubleNickel-gray-500" />
                    </ActionIcon>
                  </div>
                ))}
                {(!question.options || question.options.length === 0) && (
                  <Text size="xs" className="pl-2 text-doubleNickel-gray-500">
                    No options added yet. Click + to add options.
                  </Text>
                )}
              </div>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  )
}

interface QuestionsSectionProps {
  opened: boolean
  questions: QuestionConfig[]
  onQuestionsChange: (questions: QuestionConfig[]) => void
  activeSection: string | null
  onActiveSectionChange: (section: string | null) => void
}

const QuestionsSection = ({
  opened,
  questions,
  onQuestionsChange,
  activeSection,
  onActiveSectionChange,
}: QuestionsSectionProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Fetch active custom questions for the dropdown
  const { data: questionsData, loading: questionsLoading } = useQuery(
    GET_CUSTOM_QUESTIONS,
    {
      variables: {
        filters: { statuses: ['ACTIVE'] },
        pageInfo: { limit: 100, offset: 0 },
      },
      skip: !opened,
      onError: (error) => {
        toast('Error loading questions', 'error')
      },
    }
  )

  const customQuestions = questionsData?.customQuestions?.items ?? []

  // Filter out questions that are already added to the template
  const addedQuestionIds = new Set(
    questions.filter((q) => q.customQuestionId).map((q) => q.customQuestionId)
  )
  const availableQuestions = customQuestions.filter(
    (q) => !addedQuestionIds.has(q.customQuestionId)
  )

  const handleQuestionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = questions.findIndex((item) => item.id === active.id)
      const newIndex = questions.findIndex((item) => item.id === over.id)
      onQuestionsChange(arrayMove(questions, oldIndex, newIndex))
    }
  }

  const handleAddQuestion = () => {
    const newQuestion: QuestionConfig = {
      id: `new-${Date.now()}`,
      label: '',
      fieldType: CustomQuestionFieldType.SHORT_TEXT,
      isRequired: false,
      visibility: CustomQuestionVisibility.PRIVATE,
      order: questions.length,
      isOpen: true,
    }
    onQuestionsChange([...questions, newQuestion])
  }

  const handleQuestionLabelChange = (id: string, label: string) => {
    onQuestionsChange(questions.map((q) => (q.id === id ? { ...q, label } : q)))
  }

  const handleQuestionKeyChange = (id: string, key: string) => {
    onQuestionsChange(questions.map((q) => (q.id === id ? { ...q, key } : q)))
  }

  const handleQuestionFieldTypeChange = (id: string, fieldType: string) => {
    onQuestionsChange(
      questions.map((q) => (q.id === id ? { ...q, fieldType } : q))
    )
  }

  const handleQuestionIsRequiredChange = (id: string, isRequired: boolean) => {
    onQuestionsChange(
      questions.map((q) => (q.id === id ? { ...q, isRequired } : q))
    )
  }

  const handleQuestionVisibilityChange = (
    id: string,
    visibility: CustomQuestionVisibility
  ) => {
    onQuestionsChange(
      questions.map((q) => (q.id === id ? { ...q, visibility } : q))
    )
  }

  const handleQuestionOptionsChange = (
    id: string,
    options: QuestionOption[]
  ) => {
    onQuestionsChange(
      questions.map((q) => (q.id === id ? { ...q, options } : q))
    )
  }

  const handleAddExistingQuestion = (question: any) => {
    const sortedOptions = [...(question.options || [])]
      .sort((a, b) => a.order - b.order)
      .map((opt) => ({
        id: opt.customQuestionOptionId,
        label: opt.label,
        order: opt.order,
      }))

    const newQuestion: QuestionConfig = {
      id: `existing-${question.customQuestionId}-${Date.now()}`,
      customQuestionId: question.customQuestionId,
      label: question.label,
      key: question.key,
      fieldType: question.fieldType,
      isRequired: false,
      visibility: CustomQuestionVisibility.PRIVATE,
      options: sortedOptions,
      originalOptions: sortedOptions, // Store original options for comparison
      order: questions.length,
      isOpen: false,
    }
    onQuestionsChange([...questions, newQuestion])
  }

  const handleToggleQuestion = (id: string) => {
    onQuestionsChange(
      questions.map((q) => (q.id === id ? { ...q, isOpen: !q.isOpen } : q))
    )
  }

  const handleDeleteQuestion = (id: string) => {
    onQuestionsChange(questions.filter((q) => q.id !== id))
  }

  const questionCount = questions.length

  return (
    <Accordion
      value={activeSection}
      onChange={onActiveSectionChange}
      styles={{
        root: {
          padding: 0,
        },
        item: {
          border: 'none',
        },
        control: {
          padding: 0,
        },
        content: {
          padding: 0,
        },
      }}
    >
      <Accordion.Item value="questions">
        <Accordion.Control>
          <div className="flex items-center justify-between">
            <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
              QUESTIONS
            </Text>
            <Text className="mr-2 text-xs text-doubleNickel-gray-500">
              {`${questionCount} ${
                questionCount <= 1 ? 'question' : 'questions'
              }`}
            </Text>
          </div>
        </Accordion.Control>
        <Accordion.Panel>
          <div className="flex flex-col gap-3 pt-3">
            <div className="flex items-center justify-between">
              <Text className="text-xs text-doubleNickel-gray-600">
                Toggle questions on or off and drag to reorder them for this
                template.
              </Text>
              <Menu shadow="md" position="bottom-end" width={250}>
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    className="text-doubleNickel-gray-600"
                  >
                    <IconAdd
                      className="h-4 w-4"
                      stroke="currentColor"
                      fill="none"
                    />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    className="text-sm text-doubleNickel-gray-900"
                    onClick={handleAddQuestion}
                  >
                    Create Question
                  </Menu.Item>
                  {availableQuestions.length > 0 && <Menu.Divider />}
                  {questionsLoading ? (
                    <Menu.Item disabled>Loading questions...</Menu.Item>
                  ) : availableQuestions.length > 0 ? (
                    availableQuestions.map((question) => (
                      <Menu.Item
                        key={question.customQuestionId}
                        className="text-sm text-doubleNickel-gray-700"
                        onClick={() => handleAddExistingQuestion(question)}
                      >
                        {question.label}
                      </Menu.Item>
                    ))
                  ) : (
                    <Menu.Item disabled className="text-sm">
                      No questions available
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            </div>
            <div className="space-y-1 rounded-lg border border-gray-200 p-3">
              {questions.length === 0 ? (
                <Text className="text-sm text-doubleNickel-gray-500">
                  No questions added yet.
                </Text>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleQuestionDragEnd}
                >
                  <SortableContext
                    items={questions.map((q) => q.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {questions.map((question) => (
                      <SortableQuestionItem
                        key={question.id}
                        question={question}
                        onLabelChange={handleQuestionLabelChange}
                        onKeyChange={handleQuestionKeyChange}
                        onFieldTypeChange={handleQuestionFieldTypeChange}
                        onIsRequiredChange={handleQuestionIsRequiredChange}
                        onVisibilityChange={handleQuestionVisibilityChange}
                        onOptionsChange={handleQuestionOptionsChange}
                        onToggle={handleToggleQuestion}
                        onDelete={handleDeleteQuestion}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  )
}

export default QuestionsSection
